import React, { useEffect, useState } from 'react'
import { Formik, Form, FieldArray, Field } from 'formik'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import SharedButton from '../../components/Shared/SharedButton'
import TextInput from '../../components/Shared/TextInput'
import AutoCompleteField from '../../components/Shared/AutoCompleteField'
import DropdownComponent from '../../components/Shared/DropdownComponent'
import CustomSearchInput from '../../components/Shared/CustomSearchInput'
import PageHeader from '../../components/Shared/PageHeader'
import i18next from 'i18next'
import { Button } from 'primereact/button'
import toast from 'react-hot-toast'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchData } from '../../helpers/fetchData'
import validSupergitHIS from '../../api/validSupergitHIS'
import { RiDeleteBin5Line } from 'react-icons/ri'
import { FaCheckDouble } from 'react-icons/fa6'

const amountTypeOptions = [
  { label: 'Patient Share', value: 'patient_share' },
  { label: 'Company Share', value: 'company_share' },
  { label: 'Patient Vat', value: 'patient_vat' },
  { label: 'Company Vat', value: 'company_vat' },
  { label: 'Gross Amount', value: 'gross_amount' },
  { label: 'Discount Amount', value: 'discount_amount' },
  { label: 'Total Vat', value: 'total_vat' },
  { label: 'Total Amount With Vat', value: 'total_amount_with_vat' },
  { label: 'Total Amount Without Vat', value: 'total_amount_without_vat' }
]

const transactionTypeOptions = [
    { label: 'Debit', value: 'debit' },
    { label: 'Credit', value: 'credit' }
]

const accountTypeOptions = [
    { label: 'General', value: 'general' },
    { label: 'Patient', value: 'patient' },
    { label: 'Company', value: 'company' }
]

const PayerSettingsPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const lang = i18next.language
  const editData = location?.state?.editData
  const [loading, setLoading] = useState(false)
  const [businessOptions, setBusinessOptions] = useState([])
  const { t } = useTranslation()

  const modalTitle = () => {
    let title = editData ? 'update payer settings' : 'create payer settings'
    return t(title)
  }

  const fetchBusinessOptions = async () => {
    try {
      const res = await validSupergitHIS.post('getCompanies', { page: 1, per_page: 100 })
      if (res?.data?.data) {
        const options = res.data.data.map(item => ({
          label: lang === 'ar' ? item.name_ar : item.name_en,
          value: item.id,
          // Store full company data for payload
          payer_id: String(item.id),
          payer_name: lang === 'ar' ? item.name_ar : item.name_en,
          payer_type: item.type || '',
          payer_license_id: item.license_id || '',
          payer_tpa_id: String(item.tpa_id || ''),
          payer_tpa_license_id: item.tpa_license_id || ''
        }))
        setBusinessOptions(options)
      }
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    fetchBusinessOptions()
  }, [])

  const initialValues = {
    payer_id: editData?.payer_id ? Number(editData.payer_id) : '',
    payer_name: editData?.payer_name ?? '',
    payer_type: editData?.payer_type ?? '',
    payer_license_id: editData?.payer_license_id ?? '',
    payer_tpa_id: editData?.payer_tpa_id ?? '',
    payer_tpa_license_id: editData?.payer_tpa_license_id ?? '',
    account: '',
    accounts: editData?.transactions || []
  }

  const validationSchema = yup.object().shape({
    payer_id: yup.string().required(t('required')),
    accounts: yup.array().of(
      yup.object().shape({
        account_code: yup.string().required(t('required')),
        transaction_type: yup
          .string()
          .oneOf(['credit', 'debit'], t('invalid_value'))
          .required(t('required')),
        amount_type: yup.string().required(t('required'))
      })
    )
  })

  const handleCreateSettings = async payload => {
    try {
      setLoading(true)
      const res = await fetchData('POST', '/tsetting/payer/create', payload)
      toast.success(res?.message)
      if (res?.message == 'Transaction setting created successfully') {
        navigate(-1)
      }
    } catch (error) {
      toast.error(error.message || t('failed_to_create_settings'))
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateSettings = async payload => {
    try {
      setLoading(true)
      const res = await fetchData('PUT', '/tsetting/update', payload)
      toast.success(res?.message)
      if (res?.message == 'Transaction setting updated successfully') {
        navigate(-1)
      }
    } catch (error) {
      toast.error(error.message || t('failed_to_update_settings'))
    } finally {
      setLoading(false)
    }
  }

  const handleFormSubmit = values => {
    const transactions = values?.accounts || []

    const hasCredit = transactions.some(t => t?.transaction_type === 'credit')
    const hasDebit = transactions.some(t => t?.transaction_type === 'debit')

    if (!hasCredit || !hasDebit) {
      const missingType = !hasDebit ? 'debit' : 'credit'
      toast.error(`There should be at least one ${missingType} account`)
      return
    }

    if (values?.accounts.length < 1) {
      toast.error('Please Select Account')
      return
    }

    let payload = {
      ...(editData && { id: editData.id }),
      title: 'payer_setting',
      payer_id: String(values?.payer_id || ''),
      payer_name: values?.payer_name || '',
      payer_type: values?.payer_type || '',
      payer_license_id: values?.payer_license_id || '',
      payer_tpa_id: String(values?.payer_tpa_id || ''),
      payer_tpa_license_id: values?.payer_tpa_license_id || '',
      ...(editData && { is_active: editData?.is_active }),
      transactions: values?.accounts.map(acc => ({
        ...acc,
        account_code: String(acc?.account_code),
        account_id: acc?.account_id ? String(acc?.id) : undefined
      }))
    }

    if (editData) {
      handleUpdateSettings(payload)
    } else {
      handleCreateSettings(payload)
    }
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize={true}
      >
        {props => (
          <Form onSubmit={props.handleSubmit}>
            <div className='flex flex-col w-full'>
              <PageHeader title={modalTitle()} backPath={-1} />
              
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 w-full'>
                <div>
                  <CustomSearchInput
                    label={t('Payer')}
                    options={businessOptions}
                    name={'payer_id'}
                    onChange={e => {
                      // e is the selected option object { label, value, ... }
                      if(e) {
                        props?.setFieldValue('payer_id', e.value)
                        props?.setFieldValue('payer_name', e.payer_name || '')
                        props?.setFieldValue('payer_type', e.payer_type || '')
                        props?.setFieldValue('payer_license_id', e.payer_license_id || '')
                        props?.setFieldValue('payer_tpa_id', e.payer_tpa_id || '')
                        props?.setFieldValue('payer_tpa_license_id', e.payer_tpa_license_id || '')
                      } else {
                        // Handle clearing if needed
                        props?.setFieldValue('payer_id', '')
                        props?.setFieldValue('payer_name', '')
                        props?.setFieldValue('payer_type', '')
                        props?.setFieldValue('payer_license_id', '')
                        props?.setFieldValue('payer_tpa_id', '')
                        props?.setFieldValue('payer_tpa_license_id', '')
                      }
                    }}
                    value={businessOptions.find(opt => opt.value === props?.values?.payer_id)}
                    placeholder={`${t('select')} ${t('Payer')}`}
                    className='!w-full'
                  />
                  {props.errors.payer_id && props.touched.payer_id && (
                    <div className='text-red-500 text-sm mt-1'>{props.errors.payer_id}</div>
                  )}
                </div>
                
                <AutoCompleteField
                  label={t('account')}
                  name={'account'}
                  fieldUrl='searchAccounts'
                  placeholder={`${t('select')} ${t('account')}`}
                  className='!w-full !hover:border-primary !outline-none !shadow-none'
                  inputClassName={'!h-[42px]'} 
                  disabled={!props?.values?.payer_id}
                  optionsFormat={data =>
                    Object.entries(data || {}).map(([id, account]) => ({
                      label:
                        lang === 'ar'
                          ? `${account?.name_ar} ${account?.code}`
                          : `${account?.name_en} ${account?.code}`,
                      value: Number(account?.id),
                      code: account?.code,
                      name_ar: account?.name_ar,
                      name_en: account?.name_en
                    }))
                  }
                  onselect={e => {
                    const selected = e.value || e;
                    if (!selected) return;

                    const name = selected.name_en || '';
                    const [first] = name.split('-');
                    
                    let type = 'general';
                    if (first && typeof first === 'string') {
                        const trimmedFirst = first.trim().toLowerCase();
                        if (trimmedFirst === 'patient') type = 'patient';
                        else if (trimmedFirst === 'company') type = 'company';
                    }

                    const newObj = {
                      account_type: type,
                      account_code: selected.code,
                      account_name_en: selected.name_en,
                      account_name_ar: selected.name_ar,
                      transaction_type: '',
                      amount_type: ''
                    }
                    const updatedAccounts = [
                      ...(props.values.accounts || []),
                      newObj
                    ]
                    props.setFieldValue('accounts', updatedAccounts)
                    setTimeout(() => props.setFieldValue('account', ''), 100)
                  }}
                  onSelect={e => {
                     const selected = e.value || e;
                    if (!selected) return;

                    const name = selected.name_en || '';
                    const [first] = name.split('-');
                    
                    let type = 'general';
                    if (first && typeof first === 'string') {
                        const trimmedFirst = first.trim().toLowerCase();
                        if (trimmedFirst === 'patient') type = 'patient';
                        else if (trimmedFirst === 'company') type = 'company';
                    }

                    const newObj = {
                      account_type: type,
                      account_code: selected.code,
                      account_name_en: selected.name_en,
                      account_name_ar: selected.name_ar,
                      transaction_type: '',
                      amount_type: ''
                    }
                     // Check if already added? Maybe allows duplicates as per original code
                    const updatedAccounts = [
                      ...(props.values.accounts || []),
                      newObj
                    ]
                    props.setFieldValue('accounts', updatedAccounts)
                    setTimeout(() => props.setFieldValue('account', ''), 100)
                  }}
                />
              </div>

              {props?.values?.accounts?.length > 0 && (
                <FieldArray name='accounts'>
                  {({ push, remove, form }) => (
                    <div className='overflow-auto'>
                      <table className='w-full mt-2 border !rounded-md border-gray-200 whitespace-nowrap'>
                        <thead className=''>
                          <tr className='!bg-gray-300'>
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>{t('Account Type')}</th>
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>{t('Account Code')}</th>
                            {lang === 'ar' ? (
                              <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>{t('Account Name (AR)')}</th>
                            ) : (
                              <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>{t('Account Name (EN)')}</th>
                            )}
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>{t('Transaction Type')}</th>
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>{t('Amount Type')}</th>
                            <th className='table-header bg-gray-300 !font-bold px-4 py-2 text-left'>{t('actions')}</th>
                          </tr>
                        </thead>
                        <tbody className='rounded'>
                          {form.values.accounts.map((account, index) => (
                            <tr key={index} className='border-t'>
                              <td className='!px-[0.35rem] !py-[0.6rem]'>
                                <DropdownComponent
                                  disabled
                                  options={accountTypeOptions}
                                  name={`accounts[${index}].account_type`}
                                  value={props?.values?.accounts[index]?.account_type}
                                  placeholder='Select Account Type'
                                  className='!w-full'
                                />
                              </td>
                              <td className='!px-[0.35rem] !py-[0.6rem]'>
                                <Field
                                  name={`accounts[${index}].account_code`}
                                  as={TextInput}
                                  placeholder='Enter account code'
                                  disabled
                                  className='!p-1.5'
                                />
                              </td>
                              {lang === 'ar' ? (
                                <td className='!px-[0.35rem] !py-[0.6rem]'>
                                  <Field
                                    name={`accounts[${index}].account_name_ar`}
                                    as={TextInput}
                                    placeholder='Enter account name'
                                    disabled
                                    className='!p-1.5'
                                  />
                                </td>
                              ) : (
                                <td className='!px-[0.35rem] !py-[0.6rem]'>
                                  <Field
                                    name={`accounts[${index}].account_name_en`}
                                    as={TextInput}
                                    placeholder='Enter account name'
                                    disabled
                                    className='!p-1.5'
                                  />
                                </td>
                              )}
                              <td className='!px-[0.35rem] !py-[0.6rem]'>
                                <DropdownComponent
                                  options={transactionTypeOptions}
                                  name={`accounts[${index}].transaction_type`}
                                  value={props?.values?.accounts[index]?.transaction_type}
                                  onChange={e => {
                                    props?.setFieldValue(
                                      `accounts[${index}].transaction_type`,
                                      e.value
                                    )
                                  }}
                                  placeholder='Select Transaction Type'
                                  className='!w-full'
                                />
                                {props.errors.accounts?.[index]?.transaction_type &&
                                  props.touched.accounts?.[index]?.transaction_type && (
                                    <div className='text-red-500 text-sm'>
                                      {props.errors.accounts[index].transaction_type}
                                    </div>
                                  )}
                              </td>
                              <td className='!px-[0.35rem] !py-[0.6rem]'>
                                <DropdownComponent
                                  options={amountTypeOptions}
                                  name={`accounts[${index}].amount_type`}
                                  value={props?.values?.accounts[index]?.amount_type}
                                  placeholder='Select Amount Type'
                                  onChange={e => {
                                    props?.setFieldValue(
                                      `accounts[${index}].amount_type`,
                                      e.value
                                    )
                                  }}
                                  className='!w-full'
                                />
                                {props.errors.accounts?.[index]?.amount_type &&
                                  props.touched.accounts?.[index]?.amount_type && (
                                    <div className='text-red-500 text-sm'>
                                      {props.errors.accounts[index].amount_type}
                                    </div>
                                  )}
                              </td>
                              <td className='!px-[0.35rem] !py-[0.6rem]'>
                                <div className='flex justify-center items-center h-full'>
                                  <RiDeleteBin5Line
                                    size={20}
                                    className='cursor-pointer text-red-500'
                                    onClick={() => remove(index)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </FieldArray>
              )}

              <div className='flex justify-end gap-2 mt-2'>
                <SharedButton
                    icon='pi pi-times'
                    label={t('Cancel')}
                    buttonType='reset'
                    onClick={() => {
                        navigate(-1);
                    }}
                />
                <SharedButton
                    icon={<FaCheckDouble size={12} className='me-2' />}
                    label={t('Submit')}
                    className='submit-btn'
                    type='submit'
                    disabled={loading}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default PayerSettingsPage
